<template>
  <div class="w-full bg-white py-6">
    <div class="md:mx-32 mx-5">
      <!-- Search Bar -->
      <div class="flex flex-col gap-4">
        <!-- Main Search Input -->
        <div class="relative">
          <div class="flex items-center bg-gray-50 border border-gray-300 rounded-lg overflow-hidden hover:border-primary transition duration-300">
            <input
              v-model="searchQuery"
              @input="handleSearch"
              @keyup.enter="performSearch"
              type="text"
              placeholder="Search products by name..."
              class="w-full px-4 py-3 bg-gray-50 text-gray-800 focus:outline-none text-sm"
            />
            <button
              @click="performSearch"
              class="px-6 py-3 bg-primary text-white hover:bg-opacity-90 transition duration-300 flex items-center space-x-2"
            >
              <fa-icon icon="search" />
              <span class="hidden sm:inline">Search</span>
            </button>
          </div>

          <!-- Search Suggestions Dropdown -->
          <transition
            enter-active-class="transition duration-150 ease-out transform"
            enter-from-class="scale-95 opacity-0"
            enter-to-class="scale-100 opacity-100"
            leave-active-class="transition duration-150 ease-in transform"
            leave-from-class="scale-100 opacity-100"
            leave-to-class="scale-95 opacity-0"
          >
            <div
              v-if="showSuggestions && filteredProducts.length > 0"
              class="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-96 overflow-y-auto"
            >
              <div
                v-for="product in filteredProducts.slice(0, 5)"
                :key="product.id"
                @click="selectProduct(product)"
                class="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 flex items-center space-x-3"
              >
                <img
                  :src="baseProductUrl + product.images[0].url"
                  alt="product"
                  class="w-10 h-10 object-cover rounded"
                />
                <div class="flex-1">
                  <p class="text-sm font-medium text-gray-800">{{ product.name }}</p>
                  <p class="text-xs text-primary font-semibold">${{ product.price }}</p>
                </div>
              </div>
            </div>
          </transition>
        </div>

        <!-- Filter Options -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
          <!-- Category Filter -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 mb-2">Category</label>
            <select
              v-model="filters.category"
              @change="applyFilters"
              class="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:border-primary transition duration-300"
            >
              <option value="">All Categories</option>
              <option value="Women">Women</option>
              <option value="Men">Men</option>
              <option value="Kids">Kids</option>
            </select>
          </div>

          <!-- Type Filter -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 mb-2">Type</label>
            <select
              v-model="filters.type"
              @change="applyFilters"
              class="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 focus:outline-none focus:border-primary transition duration-300"
            >
              <option value="">All Types</option>
              <option value="Shoes">Shoes</option>
              <option value="Watches">Watches</option>
              <option value="Handbags">Handbags</option>
              <option value="Jewellery">Jewellery</option>
              <option value="Party">Party</option>
            </select>
          </div>

          <!-- Min Price Filter -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 mb-2">Min Price: ${{ filters.minPrice }}</label>
            <input
              v-model.number="filters.minPrice"
              @change="applyFilters"
              type="range"
              min="0"
              max="1000"
              step="10"
              class="w-full"
            />
          </div>

          <!-- Max Price Filter -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 mb-2">Max Price: ${{ filters.maxPrice }}</label>
            <input
              v-model.number="filters.maxPrice"
              @change="applyFilters"
              type="range"
              min="0"
              max="1000"
              step="10"
              class="w-full"
            />
          </div>
        </div>

        <!-- Clear Filters Button -->
        <div class="flex justify-end">
          <button
            v-if="isFiltered"
            @click="clearAllFilters"
            class="px-4 py-2 text-sm text-primary border border-primary rounded-lg hover:bg-primary hover:text-white transition duration-300"
          >
            Clear Filters
          </button>
        </div>
      </div>
    </div>

    <!-- Results Section -->
    <div v-if="displayedProducts.length > 0" class="md:mx-32 mx-5 mt-10">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-2xl font-semibold text-gray-800">
          Search Results ({{ displayedProducts.length }})
        </h2>
        <p class="text-sm text-gray-600">
          <span v-if="searchQuery">for "{{ searchQuery }}"</span>
          <span v-if="isFiltered"> with active filters</span>
        </p>
      </div>

      <!-- Products Grid -->
      <div class="grid lg:grid-cols-4 grid-cols-2 gap-3 mb-10">
        <div
          v-for="product in displayedProducts"
          :key="product.id"
          class="bg-white rounded-lg shadow hover:shadow-lg transition duration-300 overflow-hidden cursor-pointer"
          @click="goToProduct(product.id)"
        >
          <div class="relative bg-gray-100 h-48 md:h-56 overflow-hidden group">
            <img
              :src="baseProductUrl + product.images[0].url"
              :alt="product.name"
              class="w-full h-full object-cover group-hover:scale-110 transition duration-300"
            />
            <div class="absolute top-2 right-2 bg-primary text-white px-2 py-1 rounded text-xs font-semibold">
              Sale
            </div>
          </div>
          <div class="p-4">
            <h3 class="font-semibold text-gray-800 text-sm line-clamp-2 mb-2">{{ product.name }}</h3>
            <p class="text-primary font-bold text-lg">${{ product.price }}</p>
            <p class="text-xs text-gray-500 mt-1">Stock: {{ product.quantity }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- No Results -->
    <div v-else-if="hasSearched" class="md:mx-32 mx-5 mt-10 text-center py-10">
      <fa-icon icon="search" size="3x" class="text-gray-300 mb-4" />
      <p class="text-gray-600 text-lg">No products found</p>
      <p class="text-gray-400 text-sm mt-2">Try adjusting your search or filter criteria</p>
    </div>
  </div>
</template>

<script>
export default {
  name: "SearchBar",
  data() {
    return {
      searchQuery: "",
      showSuggestions: false,
      hasSearched: false,
      filters: {
        category: "",
        type: "",
        minPrice: 0,
        maxPrice: 1000,
      },
      baseProductUrl: this.$store.state.baseProductUrl,
    };
  },
  computed: {
    products() {
      return this.$store.state.products.products || [];
    },
    filteredProducts() {
      return this.$store.state.products.filteredProducts || [];
    },
    displayedProducts() {
      return this.$store.getters["products/displayedProducts"] || [];
    },
    isFiltered() {
      return (
        this.filters.category ||
        this.filters.type ||
        this.filters.minPrice > 0 ||
        this.filters.maxPrice < 1000
      );
    },
  },
  methods: {
    async handleSearch() {
      if (this.searchQuery.trim().length > 0) {
        this.showSuggestions = true;
        await this.$store.dispatch("products/searchProducts", this.searchQuery);
      } else {
        this.showSuggestions = false;
      }
    },
    async performSearch() {
      if (this.searchQuery.trim().length > 0) {
        this.showSuggestions = false;
        this.hasSearched = true;
        await this.$store.dispatch("products/searchProducts", this.searchQuery);
        setTimeout(() => {
          const resultsSection = document.querySelector('.grid');
          if (resultsSection) {
            resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    },
    selectProduct(product) {
      this.showSuggestions = false;
      this.$router.push(`/single/${product.id}`);
    },
    goToProduct(productId) {
      this.$router.push(`/single/${productId}`);
    },
    async applyFilters() {
      this.hasSearched = true;
      await this.$store.dispatch("products/advancedFilter", {
        search: this.searchQuery,
        category: this.filters.category,
        type: this.filters.type,
        minPrice: this.filters.minPrice,
        maxPrice: this.filters.maxPrice,
      });
    },
    clearAllFilters() {
      this.searchQuery = "";
      this.filters = {
        category: "",
        type: "",
        minPrice: 0,
        maxPrice: 1000,
      };
      this.hasSearched = false;
      this.$store.dispatch("products/clearFilters");
    },
  },
  mounted() {
    this.$store.dispatch("products/fetchProducts");
    
    // Check if there's a search query in URL
    const queryParam = this.$route.query.q;
    if (queryParam) {
      this.searchQuery = queryParam;
      this.performSearch();
    }
  },
};
</script>

<style scoped>
/* Custom range input styling */
input[type="range"] {
  accent-color: var(--primary, #000);
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
